<script lang="ts" setup>
import DrawerPages from '@/components/drawer/DrawerPages.vue'
import Elements from '@/components/drawer/DroppableElements.vue'
import PagesLoop from '@/components/editor/PagesLoop.vue'
import { useBuilderStore } from '@/store/builder'
import { UISpin } from '@gohighlevel/ghl-ui'
import { computed, onMounted, ref, watch, onBeforeUnmount } from 'vue'
import DrawerSettings from '../components/drawer/DrawerSettings.vue'
import DrawerVariable from '../components/drawer/DrawerVariable.vue'
import DrawerTemplateSettings from '@/components/drawer/DrawerTemplateSettings.vue'
import DrawerPaymentSettings from '../components/drawer/DrawerPaymentSettings/DrawerPaymentSettings.vue'
import EditorMainHeader from '../components/header-components/EditorMainHeader.vue'
import EditorSubHeading from '../components/header-components/EditorSubHeading.vue'
import StyleManager from '../components/styles-manager/StyleManager.vue'
import { useActionBarStore } from '@/store/actionBar'
import { useDocument } from '@/composition'
import { useAppStore } from '@/store/app'
import { usePostmate } from '@/composition/usePostmate'
import { useUsers } from '@/composition/useUsers'
import { usePayment } from '@/composition/usePayment'
import HiddenDroppableElements from '@/components/drawer/HiddenDroppableElements.vue'
import UnsavedChangesModal from '@/components/common/UnsavedChangesModal.vue'
import DrawerContentLibrary from '@/components/drawer/DrawerContentLibrary/DrawerContentLibrary.vue'
import {
  useCustomValues,
  useCustomFieldsForLinkage,
  initializeFonts,
} from '@gohighlevel/ghl-proposals-common'
import { useRoute } from 'vue-router'
import { union } from 'lodash'

const route = useRoute()
const pageLoading = ref(false)
const builderStore = useBuilderStore()
const appStore = useAppStore()
const store = useActionBarStore()
const { fetchUser } = useUsers()
const { getCustomFieldsForLinkage } = useCustomFieldsForLinkage(route)
const { getCustomValuesAndFieldsForLocation } = useCustomValues()

const { redirectOnParentRoute } = usePostmate()
const { getCurrencyDetails, fetchPaymentIntegrations } = usePayment()

const { loadDocument, loading, fetchAllCustomValues, documentVersion } =
  useDocument()
const postmate = usePostmate()
const isRouteChangeConfirmed = computed(
  () => postmate.isRouteChangeConfirmed.value
)
const hasUnSavedChanges = computed(() => appStore.unsavedChanges)
if (process.env.NODE_ENV === 'development') {
  // Postmate.debug = true;
}

const showUnsavedChangesModal = ref(false)
let pendingNavigation: (() => void) | null = null

const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (hasUnSavedChanges.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

const handleNavigation = (callback: () => void) => {
  if (hasUnSavedChanges.value && !builderStore.isPublished) {
    pendingNavigation = callback
    showUnsavedChangesModal.value = true
  } else {
    callback()
  }
}

const handleNavigationConfirm = () => {
  if (pendingNavigation) {
    pendingNavigation()
    pendingNavigation = null
  }
}

const handleNavigationCancel = () => {
  pendingNavigation = null
}

const handleBack = () => {
  handleNavigation(() => {
    redirectOnParentRoute({
      name: 'proposals-estimates-templates',
      params: { locationId: route.params.locationId },
    })
  })
}

const loadData = async () => {
  await loadDocument().then(() => {
    fetchAllCustomValues()
  })
}

const fetchLoggedUserData = async () => {
  const userData = await fetchUser(appStore.userId)
  appStore.updateLoggedInUserData(userData)
}

onMounted(async () => {
  pageLoading.value = true
  await loadData()
  await getCurrencyDetails()
  await fetchLoggedUserData()
  await getCustomValuesAndFieldsForLocation()
  await getCustomFieldsForLinkage()
  if (documentVersion.value > 1) {
    const popularFonts = await initializeFonts([])
    builderStore.loadedFonts = union(
      ...(builderStore.loadedFonts || []),
      ...(builderStore.fontsToLoad || []),
      popularFonts
    )
  }
  await fetchPaymentIntegrations()
  pageLoading.value = false
  window.addEventListener('beforeunload', handleBeforeUnload)
})

const showModal = computed({
  get: () => showUnsavedChangesModal.value && !builderStore.isPublished,
  set: (value: boolean) => {
    showUnsavedChangesModal.value = value
  },
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

watch([hasUnSavedChanges, isRouteChangeConfirmed], () => {
  if (hasUnSavedChanges.value && !isRouteChangeConfirmed.value) {
    window.addEventListener('beforeunload', handleBeforeUnload)
  } else {
    window.removeEventListener('beforeunload', handleBeforeUnload)
  }
})
</script>

<template>
  <div
    v-if="pageLoading"
    class="flex justify-center items-center justify-items-center h-screen"
  >
    <UISpin :show="pageLoading" />
  </div>
  <template v-else>
    <div
      v-if="loading"
      class="flex justify-center items-center justify-items-center h-screen absolute w-screen z-[99999] bg-white bg-opacity-80"
    >
      <UISpin :show="true" />
    </div>
    <UnsavedChangesModal
      v-model:show="showModal"
      @onCancel="handleNavigationCancel"
      @onOk="handleNavigationConfirm"
    />
    <EditorMainHeader @onBack="handleBack" />
    <div>
      <EditorSubHeading />
      <div class="flex w-full editor-area relative">
        <div id="drawer-target"></div>
        <DrawerVariable />
        <DrawerSettings />
        <DrawerPages />
        <Elements />
        <DrawerPaymentSettings />
        <DrawerTemplateSettings />
        <DrawerContentLibrary />
        <HiddenDroppableElements />
        <div class="flex w-full" @click="store.closeDrawer()">
          <PagesLoop
            class="flex flex-col items-center overflow-auto elements_page_parent flex-1 bg-gray-100 pb-8"
          />
        </div>
        <template v-if="builderStore.isDocumentEditable">
          <div id="active-element-drawer-target"></div>
          <StyleManager class="w-96 overflow-y-auto" />
        </template>
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
.proposal-editor {
  display: inline-flex;
  height: 20rem;
}
.editor-area {
  height: calc(100vh - 110px);
}

.proposal-editor {
  display: inline-flex;
  height: 20rem;
}
.elements_page_parent {
  padding-top: 1rem;
  margin-bottom: 1rem;
}
</style>
